<script setup lang="ts" generic="T extends string | number">
export interface SegmentedOption<TValue = string> {
  value: TValue;
  label: string;
  /** Colour of the selected segment. Defaults to `primary`. */
  variant?: 'primary' | 'success' | 'warning' | 'neutral';
  disabled?: boolean;
}

const props = withDefaults(
  defineProps<{
    options: SegmentedOption<T>[];
    modelValue: T | undefined;
    ariaLabel?: string;
    disabled?: boolean;
    /** Fill the available width, splitting it evenly between the segments. */
    block?: boolean;
    size?: 'small' | 'default';
  }>(),
  {
    ariaLabel: undefined,
    size: 'default',
  },
);

const emit = defineEmits<{
  (e: 'select', value: T): void;
}>();

function select(option: SegmentedOption<T>) {
  if (props.disabled || option.disabled || option.value === props.modelValue) {
    return;
  }
  emit('select', option.value);
}
</script>

<template>
  <div
    class="ui-segmented-control"
    :class="[`is-${props.size}`, { 'is-block': props.block }]"
    role="group"
    :aria-label="props.ariaLabel"
  >
    <button
      v-for="option in props.options"
      :key="String(option.value)"
      type="button"
      class="segment"
      :class="[
        `variant-${option.variant ?? 'primary'}`,
        { 'is-active': option.value === props.modelValue },
      ]"
      :aria-pressed="option.value === props.modelValue"
      :disabled="props.disabled || option.disabled"
      @click.prevent="select(option)"
    >
      <slot :name="String(option.value)" :option="option">
        {{ option.label }}
      </slot>
    </button>
  </div>
</template>

<style lang="scss" scoped>
.ui-segmented-control {
  display: inline-flex;
  border: 1px solid var(--color-gray-300);
  border-radius: var(--border-radius-m);
  overflow: hidden;

  &.is-block {
    display: flex;
    width: 100%;

    .segment {
      flex: 1;
    }
  }
}

.segment {
  all: unset;
  box-sizing: border-box;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: var(--space-6);
  font-weight: var(--font-weight-medium);
  color: var(--color-gray-600);
  background: var(--color-gray-0);
  cursor: pointer;
  transition:
    background-color 0.15s ease,
    color 0.15s ease;
  // The group clips, so the ring stays inside the segment; the outer segments
  // round their corners like the group, so the ring follows them.
  @include focus-ring($inset: true);

  &:first-child {
    border-top-left-radius: var(--border-radius-m);
    border-bottom-left-radius: var(--border-radius-m);
  }

  &:last-child {
    border-top-right-radius: var(--border-radius-m);
    border-bottom-right-radius: var(--border-radius-m);
  }

  & + & {
    border-left: 1px solid var(--color-gray-300);
  }

  &:hover:not(.is-active):not(:disabled) {
    background: var(--color-gray-100);
  }

  &:disabled {
    cursor: not-allowed;
    opacity: 0.6;
  }

  &.is-active {
    color: var(--color-gray-0);
    --color-focus-ring: var(--color-gray-0);
  }

  &.variant-primary.is-active {
    background: var(--color-primary-600);
  }

  &.variant-success.is-active {
    background: var(--color-success-600);
  }

  &.variant-warning.is-active {
    background: var(--color-warning-500);
    // White on this fill is 2.35:1; gray-900 is 7.56:1.
    --color-focus-ring: var(--color-gray-900);
  }

  &.variant-neutral.is-active {
    background: var(--color-gray-500);
  }
}

.is-default .segment {
  padding: var(--space-10) var(--space-16);
  font-size: var(--font-size-13);
}

.is-small .segment {
  padding: var(--space-4) var(--space-12);
  font-size: var(--font-size-12);
}
</style>
